import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

export interface SugestaoAplicativo {
    nome: string | null
    categoria: string | null
    cnpj: string | null
    nomeResponsavel: string | null
    emailResponsavel: string | null
    corPrimaria: string
    corSecundaria: string
}

export interface SituacaoAplicativo {
    idEstabelecimento: number
    possuiAplicativo: boolean
    podeCriar: boolean
    appNome?: string
    urlAcesso?: string
    sugestao?: SugestaoAplicativo
}

export interface NovoAplicativoPayload {
    nome: string
    categoria: string
    cnpj: string
    nomeResponsavel: string
    emailResponsavel: string
    corPrimaria: string
    corSecundaria: string
}

export interface AplicativoCriado {
    appNome: string
    urlAcesso: string
    urlAcesso2: string
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaErroRaw = { codErro: number; mensagem?: string }

function isErroBackend(data: unknown): data is RespostaErroRaw {
    return (
        typeof data === "object" &&
        data !== null &&
        !Array.isArray(data) &&
        "codErro" in data &&
        (data as RespostaErroRaw).codErro > 0
    )
}

function erroParaRespostaRaw(error: unknown): RespostaErroRaw {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (isErroBackend(data)) {
        return { codErro: data.codErro, mensagem: data.mensagem }
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" }
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" }
}

// Cache por estabelecimento: o Cabeçalho (presente em todas as telas) consulta a situação
// para decidir se mostra "Criar aplicativo" no menu; evita uma chamada a cada navegação.
const CHAVE_CACHE = "situacaoAplicativoEstab"

export function lerSituacaoAplicativoCache(
    idEstabelecimento: number | null
): SituacaoAplicativo | null {
    if (!idEstabelecimento) return null
    try {
        const cache = JSON.parse(sessionStorage.getItem(CHAVE_CACHE) || "null")
        if (cache && cache.idEstabelecimento === idEstabelecimento) return cache
    } catch {
        /* ignore */
    }
    return null
}

export function limparSituacaoAplicativoCache() {
    try {
        sessionStorage.removeItem(CHAVE_CACHE)
    } catch {
        /* ignore */
    }
}

export const obterSituacaoAplicativo = async function (
    authorization: string
): Promise<SituacaoAplicativo | ErroApi> {
    try {
        const response = await api
            .get<SituacaoAplicativo | RespostaErroRaw>("aplicativoDados/ObterSituacaoAplicativo", {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: authorization,
                },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        const situacao = response.data as SituacaoAplicativo
        try {
            sessionStorage.setItem(CHAVE_CACHE, JSON.stringify(situacao))
        } catch {
            /* ignore */
        }
        return situacao
    } catch (error) {
        RegistrarLogErro(error, "ObterSituacaoAplicativo", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const criarAplicativoEstabelecimento = async function (
    authorization: string,
    payload: NovoAplicativoPayload
): Promise<AplicativoCriado | ErroApi> {
    try {
        const response = await api
            .post<AplicativoCriado | RespostaErroRaw>(
                "aplicativoDados/CriarAplicativoEstabelecimento",
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authorization,
                    },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        limparSituacaoAplicativoCache()
        return response.data as AplicativoCriado
    } catch (error) {
        RegistrarLogErro(error, "CriarAplicativoEstabelecimento", payload.nome)
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
